"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useState } from "react";
import InstagramIcon from "@/assets/instagram.svg";
import LinkedInIcon from "@/assets/linkedin.svg";
import LogoHorizontalColorLight from "@/assets/logo-horizontal-color-light.svg";
import XIcon from "@/assets/xlogo.svg";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { socialLinks } from "@/constants/social-links";

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations("navigation");

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 px-4">
      <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto py-4">
        <div className="flex items-center justify-between">
          <LogoHorizontalColorLight className="h-10 md:h-14 text-black md:mr-4" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 xl:gap-8 text-m tracking-wide">
            <Link
              href="#product"
              className="hover:text-gray-600 font-medium transition-colors whitespace-nowrap"
            >
              {t("product")}
            </Link>

            <Link
              href="#technology"
              className="hover:text-gray-600 font-medium transition-colors whitespace-nowrap"
            >
              {t("technology")}
            </Link>

            <Link
              href="#app-preview"
              className="hover:text-gray-600 font-medium transition-colors whitespace-nowrap"
            >
              {t("app")}
            </Link>

            <Link
              href="/blog"
              className="hover:text-gray-600 font-medium transition-colors whitespace-nowrap"
            >
              {t("blog")}
            </Link>

            <Link
              href="#contact"
              className="hover:text-gray-600 transition-colors font-bold whitespace-nowrap"
            >
              {t("contact")}
            </Link>

            <Link
              href="#beta-tests"
              className="hover:text-gray-600 text-brand-blue transition-colors font-bold whitespace-nowrap"
            >
              {t("betaTests")}
            </Link>
            <Link
              href="/manifest"
              className="hover:text-gray-600 text-brand-red transition-colors font-bold whitespace-nowrap"
            >
              {t("manifest")}
            </Link>
            <div className="flex items-center gap-4">
              <div className="text-gray-300 leading-none">|</div>
              {/* Language Switcher */}
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile Hamburger Button */}
          {isMenuOpen && (
            <div className="md:hidden z-100 absolute left-4 animate-fade-in">
              <LanguageSwitcher />
            </div>
          )}
          <button
            type="button"
            onClick={toggleMenu}
            className="md:hidden flex flex-col justify-center items-center w-11 h-11 space-y-1.5 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 relative z-100 min-w-[44px] min-h-[44px]"
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            <span
              className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed top-0 left-0 right-0 bg-gray-50/98 backdrop-blur-sm border-b border-gray-200 transition-all duration-300 ease-in-out z-40 pt-7 ${
          isMenuOpen
            ? "transform translate-y-0 opacity-100"
            : "transform -translate-y-full opacity-0"
        }`}
      >
        <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-4 py-8">
          {/* Header with Language Switcher side by side with X button */}
          {/* <div className="flex justify-between items-center mb-6">
            <LanguageSwitcher />
          </div> */}

          <div className="flex flex-col space-y-2 lg:space-y-5 text-lg tracking-wide">
            <Link
              href="#product"
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("product")}
            </Link>
            <Link
              href="#artists"
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("artists")}
            </Link>
            <Link
              href="#collectors"
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("collectors")}
            </Link>
            <Link
              href="#museums"
              onClick={closeMenu}
              className="hover:text-gray-800 font-medium transition-colors duration-200 py-2"
            >
              {t("museums")}
            </Link>
            <Link
              href="#technology"
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("technology")}
            </Link>
            <Link
              href="#app-preview"
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("app")}
            </Link>
            <Link
              href="/blog"
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("blog")}
            </Link>
            <Link
              href="#contact"
              onClick={closeMenu}
              className="hover:text-gray-600 transition-colors font-bold py-2"
            >
              {t("contact")}
            </Link>
            <Link
              href="#beta-tests"
              onClick={closeMenu}
              className="hover:text-gray-600 text-brand-blue transition-colors font-bold py-2"
            >
              {t("betaTests")}
            </Link>
            <Link
              href="/manifest"
              onClick={closeMenu}
              className="hover:text-gray-600 text-brand-red transition-colors font-bold py-2"
            >
              {t("manifest")}
            </Link>

            {/* Social Media Links for Mobile */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-8">
                {socialLinks.map((link) => {
                  const iconMap = {
                    x: XIcon,
                    linkedin: LinkedInIcon,
                    instagram: InstagramIcon,
                    tiktok: null,
                  };

                  const Icon = iconMap[link.platform];
                  if (!Icon) return null;

                  return (
                    <a
                      key={link.platform}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link.title}
                      aria-label={link.ariaLabel}
                      className="w-10 h-10 flex items-center justify-center text-black hover:text-gray-600 transition-colors duration-200"
                      onClick={closeMenu}
                    >
                      <Icon className="w-6 h-6 fill-current" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
